import React, { JSX, useEffect, useState } from 'react'
import {
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
  Theme,
  DialogContent,
  Stack,
  IconButton,
} from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import ButtonComponent from './ButtonComponent'
import WarningIcon from '@mui/icons-material/Warning'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Emergency from './icons/Emergency'
import CloseIcon from '@mui/icons-material/Close'

const useStyles = makeStyles((theme: Theme) => createStyles({
  message: {
    wordWrap: 'break-word',
  },
  noBorder: {
    margin: 0,
    padding: 0,
  },
  baseBox: {
    position: 'relative',
  },
  outsideBox: {
    position: 'absolute',
    right: 0,
    top: 0,
  },
}))

export interface Props {
  type?: 'error' | 'success' | 'warning'
  scroll?: 'body' | 'paper'
  content?: JSX.Element
  message?: string
  title?: JSX.Element
  open: boolean
  fullWidth?: boolean
  dialogWidth?: string
  requestActions?: boolean
  onClose?: (event?: any, reason?: string) => void
  confirmLabel?: string | null
  cancelLabel?: string | null
  autoClose?: boolean
  closingTime?: number
  closeIcon?: boolean
  onConfirm?: (label?: string) => void
  onCancel?: () => void
  disabledConfirm?: boolean | undefined
  noBorder?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  disableEscapeBackdropClick?: boolean
}

const DialogComponent: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const closingTime = props.closingTime ? props.closingTime : 3000
  const dialogWidth = props.dialogWidth || 'auto'
  const requestActions = props.requestActions || false
  const confirmLabel = props.confirmLabel ? props.confirmLabel : t('crude:confirm')
  const cancelLabel = props.cancelLabel ? props.cancelLabel : t('crude:cancel')
  const [open, setOpen] = useState<boolean>(false)
  const [autoClose, setAutoClose] = useState<boolean>(typeof (props.autoClose) === 'boolean' ? props.autoClose : true)

  const handleClose = (event?: any, reason?: string) => {
    if (reason && reason === 'backdropClick' && props.disableEscapeBackdropClick) return

    props.onClose && props.onClose(event, reason)
    setOpen(false)
  }

  const onConfirm = (key?: string) => {
    setOpen(false)

    if (props.confirmLabel !== null) props.onConfirm && props.onConfirm()
  }

  const onCancel = () => {
    setOpen(false)
    props.onCancel && props.onCancel()
  }

  useEffect(() => {
    setAutoClose(typeof (props.autoClose) === 'boolean' ? props.autoClose : true)
  }, [props.autoClose])

  useEffect(() => {
    props.open ? setOpen(true) : setOpen(false)
  })

  useEffect(() => {
    if (open && autoClose) {
      setTimeout(() => {
        handleClose()
      }, closingTime)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      scroll={props.scroll || 'paper'}
      maxWidth={props.maxWidth}
      onClose={(event, reason) => handleClose(event, reason)}
      fullWidth={props.fullWidth}
      sx={{ '& .MuiDialog-paper': { borderRadius: 0 } }}>
      <Stack sx={{ p: 3 }} className={props.noBorder ? classes.noBorder : ''}>
        <Stack direction='row' spacing={3} alignItems='center' justifyContent={props.type ? 'space-between' : props.title ? 'space-between' : 'flex-end'} className={classes.baseBox}>
          <Stack direction='row' spacing={3} alignItems='center'>
            {props.type === 'error' && <WarningIcon color='error' sx={{ fontSize: 50 }} />}
            {props.type === 'warning' && <Emergency color='#ed6c02' />}
            {props.type === 'success' && <CheckCircleIcon color='success' sx={{ fontSize: 50 }} />}
            {props.title && (
              <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px' }}>
                {props.title}
              </DialogTitle>
            )}
          </Stack>

          {props.closeIcon && <IconButton sx={{ marginLeft: 'auto' }} className={props.noBorder ? classes.outsideBox : ''} onClick={() => handleClose()}><CloseIcon /></IconButton>}
        </Stack>
        {props.message && (
          <DialogContent sx={{ p: 0, pt: 2, ml: props.type ? 9.25 : 0 }}>
            <Typography className={classes.message}>{props.message}</Typography>
          </DialogContent>
        )}
        {props.content && <DialogContent sx={{ p: 0, minWidth: dialogWidth, overflow: 'visible' }}>{props.content}</DialogContent>}
      </Stack>

      {requestActions &&
        <DialogActions sx={{ p: 3 }}>
          {props.cancelLabel !== null &&
            <ButtonComponent
              color='primary'
              variant='text'
              text={cancelLabel}
              onClick={() => onCancel()}
            />}
          {props.confirmLabel !== null &&
            <ButtonComponent
              color='primary'
              variant='contained'
              text={confirmLabel}
              disabled={props.disabledConfirm}
              onClick={() => onConfirm()}
            />}
        </DialogActions>}
    </Dialog>
  )
}

export default DialogComponent
